import {
  cn,
  Tooltip as HeroTooltip,
  type TooltipContentProps,
} from "@heroui/react";
import type { ComponentProps, PointerEvent, ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Focusable } from "react-aria/Focusable";
import type { SlotsToClasses } from "src/utils/componentUtils";

interface TooltipBaseProps {
  content: ReactNode;
  /**
   * Hover delay in ms. Defaults to 300.
   *
   * Without a delay, tooltips flash open whenever the mouse just passes over
   * something on its way elsewhere, and they can cover the control the user is
   * trying to reach. 300 ms is long enough to ignore a passing cursor and short
   * enough not to feel slow. It's the lower end of NN/g's 0.3–0.5 s
   * recommendation. HeroUI's own default is 700 ms.
   *
   * - https://www.nngroup.com/articles/timing-exposing-content/
   * - https://blog.master.dev/tooltips-need-a-delay-and-then-they-need-to-skip-it/
   *
   * Keyboard focus still opens the tooltip instantly, and once one tooltip has
   * opened, nearby ones open without the delay.
   */
  delay?: number;
  closeDelay?: number;
  placement?: TooltipContentProps["placement"];
  classNames?: SlotsToClasses<"trigger" | "content">;
  showArrow?: boolean;
  isDisabled?: boolean;
  trigger?: "hover" | "focus";
  stopPropagation?: boolean;
}

export type TooltipProps = TooltipBaseProps & {
  asChild?: boolean;
  children: ComponentProps<typeof Focusable>["children"];
};

/**
 * Shows a short hint when a control is hovered or focused.
 *
 * When to use it:
 * - Don't put information here that users need to complete their task.
 * - Keep the text brief, and don't repeat the control's visible label.
 * - Add tooltips to icon-only buttons that have no text label.
 * - Use them consistently: similar controls should all have one or none.
 * - Use `showArrow` when nearby elements make it unclear which one the
 *   tooltip belongs to.
 *
 * Based on [NN/g's tooltip guidelines](https://www.nngroup.com/articles/tooltip-guidelines/).
 */
export function Tooltip({
  content,
  children,
  delay = 300, // Why not 0: see the `delay` prop comment above.
  closeDelay,
  placement,
  classNames,
  showArrow,
  isDisabled,
  trigger,
  stopPropagation = false,
  asChild = false,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const touchOpenRef = useRef(false);
  const tooltipId = useId();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleDocumentPointerDown(event: globalThis.PointerEvent) {
      const isInsideTooltip = event
        .composedPath()
        .some(
          (target) =>
            target instanceof Element &&
            target.getAttribute("data-tooltip-id") === tooltipId,
        );

      if (!isInsideTooltip) {
        touchOpenRef.current = false;
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleDocumentPointerDown,
        true,
      );
    };
  }, [isOpen, tooltipId]);

  function handleTouch(pointerEvent: PointerEvent<HTMLDivElement>) {
    if (
      pointerEvent.pointerType !== "touch" ||
      isDisabled ||
      // below guard ensures that AutoRefreshSwitch and similar wrapped controls must preserve their normal touch behavior.
      pointerEvent.currentTarget.querySelector(
        "button, input, select, textarea, [role=button], [role=checkbox], [role=switch]",
      )
    ) {
      return;
    }

    touchOpenRef.current = !touchOpenRef.current;
    const nextIsOpen = touchOpenRef.current;

    // HeroUI processes its own press event after this handler. Apply the touch
    // state once that lifecycle has completed so it cannot immediately undo it.
    setTimeout(() => setIsOpen(nextIsOpen), 0);
  }

  function handleOpenChange(nextIsOpen: boolean) {
    if (!nextIsOpen && touchOpenRef.current) {
      return;
    }

    setIsOpen(nextIsOpen);
  }

  return (
    <HeroTooltip
      delay={delay}
      closeDelay={closeDelay}
      isDisabled={isDisabled}
      trigger={trigger}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      shouldCloseOnPress={false}
    >
      {asChild ? (
        <Focusable data-tooltip-id={tooltipId}>{children}</Focusable>
      ) : (
        <HeroTooltip.Trigger
          data-tooltip-id={tooltipId}
          className={classNames?.trigger}
          onPointerDown={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPointerDownCapture={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPointerUp={handleTouch}
          onClick={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onClickCapture={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {children}
        </HeroTooltip.Trigger>
      )}
      <HeroTooltip.Content
        data-tooltip-id={tooltipId}
        placement={placement}
        className={cn(
          "!break-normal break-words whitespace-normal",
          classNames?.content,
        )}
        showArrow={showArrow}
      >
        {showArrow ? <HeroTooltip.Arrow /> : null}
        {content}
      </HeroTooltip.Content>
    </HeroTooltip>
  );
}
